import type { GridLeafColumn } from './useGridState'
import classes from './grid-theme.module.scss'

interface GridSkeletonRowProps<T> {
  rowId: string
  rowIndex: number
  leafColumns: GridLeafColumn<T>[]
  leadSpacer?: number
  trailSpacer?: number
  style?: React.CSSProperties
}

/**
 * Stands in for a row whose block has not arrived yet. The server-side model
 * knows the total row count before it knows the rows, so the grid draws the
 * right number of rows from the start and fills them in — the scrollbar never
 * jumps, and a fast scroll reads as loading rather than as an empty list.
 */
const GridSkeletonRow = <T,>({
  rowId,
  rowIndex,
  leafColumns,
  leadSpacer,
  trailSpacer,
  style,
}: GridSkeletonRowProps<T>) => (
  <div
    className={`${classes.row} ${classes.rowVirtual} ${classes.rowSkeleton}`}
    style={style}
    data-row-id={rowId}
    data-row-index={rowIndex}
    role="row"
    aria-busy="true"
  >
    {leadSpacer ? (
      <div style={{ flex: '0 0 auto', width: leadSpacer }} aria-hidden />
    ) : null}
    {leafColumns.map((leaf) => (
      <div
        key={leaf.field}
        className={classes.cell}
        style={{
          width: leaf.width,
          minWidth: leaf.width,
          maxWidth: leaf.width,
          ...(leaf.pinned === 'left' ? { left: leaf.offset } : {}),
          ...(leaf.pinned === 'right' ? { right: leaf.offset } : {}),
        }}
      >
        <span className={classes.skeletonBar} />
      </div>
    ))}
    {trailSpacer ? (
      <div style={{ flex: '0 0 auto', width: trailSpacer }} aria-hidden />
    ) : null}
  </div>
)

export default GridSkeletonRow
